/* global fbq */
import React, { useState, useEffect } from "react";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import CloseIcon from "@material-ui/icons/Close";
import { useMediaQuery } from '@material-ui/core';
import axios from "axios";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

const FREE_SHIPPING_THRESHOLD = 349;

const Checkout = (props) => {
  const isMobileView = useMediaQuery('(max-width:600px)');

  const basketItemsWithQuantity = props.itemBasket.reduce((acc, item) => {
    const index = acc.findIndex((existingItem) => existingItem.id === item.id && existingItem.size === item.size);
    if (index !== -1) {
      acc[index].quantity += 1;
    } else {
      acc.push({ ...item, quantity: 1 });
    }
    return acc;
  }, []);

  const initialQuantities = basketItemsWithQuantity.reduce(
    (acc, item) => ({ ...acc, [`${item.id}-${item.size}`]: item.quantity }),
    {}
  );
  const [itemQuantities, setItemQuantities] = useState(initialQuantities);

  const handleQuantityChange = (itemId, itemSize, delta) => {
    const key = `${itemId}-${itemSize}`;
    setItemQuantities((prev) => ({
      ...prev,
      [key]: Math.max(prev[key] + delta, 1),
    }));
  };

  const getTotalPrice = (items) =>
    items.reduce((total, item) => total + item.price * itemQuantities[`${item.id}-${item.size}`], 0);

  const [formattedItems, setFormattedItems] = useState([]);

  useEffect(() => {
    setFormattedItems(
      basketItemsWithQuantity.map((item) => ({
        price: item.price * 100,
        quantity: itemQuantities[`${item.id}-${item.size}`],
        description: item.name + ". Storlek: " + item.size,
        image_url: item.urlFrame,
      }))
    );
  }, [basketItemsWithQuantity, itemQuantities]);

  const handleRemoveItem = (itemId, itemSize) => {
    props.onRemoveItem(itemId, itemSize);
    const key = `${itemId}-${itemSize}`;
    setItemQuantities((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleCheckoutClick = () => {
    axios.post('https://backend-photo-gallery-hfzyyicqga-lz.a.run.app/create_payment_link', formattedItems)
      .then(response => { window.location.href = response.data.url; })
      .catch(error => { console.error(error); });
  };

  const totalPrice = getTotalPrice(basketItemsWithQuantity);
  const totalItems = Object.values(itemQuantities).reduce((a, b) => a + b, 0);
  const shippingProgress = Math.min((totalPrice / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const freeShipping = totalPrice >= FREE_SHIPPING_THRESHOLD;

  // ─── Empty state ───
  if (props.itemBasket.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', padding: '60px 24px',
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%', backgroundColor: '#f5f5f5',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
        </div>
        <p style={{ fontSize: '17px', fontWeight: 600, color: '#333', margin: '0 0 6px' }}>
          Kundvagnen är tom
        </p>
        <p style={{ fontSize: '13px', color: '#999', margin: 0, textAlign: 'center' }}>
          Lägg till posters för att komma igång
        </p>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      backgroundColor: '#fff',
    }}>
      {/* ── Header ── */}
      <div style={{
        padding: '24px 24px 16px', borderBottom: '1px solid #f0f0f0',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#161825' }}>
            Kundvagn
          </h2>
          <span style={{ fontSize: '13px', color: '#888' }}>
            {totalItems} {totalItems === 1 ? 'produkt' : 'produkter'}
          </span>
        </div>

        {/* Free shipping progress */}
        <div style={{ marginTop: 16 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 8,
          }}>
            <LocalShippingOutlinedIcon style={{ fontSize: 18, color: freeShipping ? '#16a34a' : '#888' }} />
            <span style={{ fontSize: '12px', color: freeShipping ? '#16a34a' : '#666', fontWeight: 500 }}>
              {freeShipping
                ? 'Du har fri frakt!'
                : `${FREE_SHIPPING_THRESHOLD - totalPrice} kr kvar till fri frakt`
              }
            </span>
          </div>
          <div style={{
            width: '100%', height: 4, backgroundColor: '#f0f0f0', borderRadius: 2,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${shippingProgress}%`, height: '100%',
              backgroundColor: freeShipping ? '#16a34a' : '#161825',
              borderRadius: 2, transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      </div>

      {/* ── Items ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {basketItemsWithQuantity.map((item) => {
          const key = `${item.id}-${item.size}`;
          const qty = itemQuantities[key];
          return (
            <div
              key={key}
              style={{
                display: 'flex', gap: '14px', padding: '16px 24px',
                borderBottom: '1px solid #f8f8f8',
              }}
            >
              {/* Thumbnail */}
              <div style={{
                width: 72, height: 90, borderRadius: '10px', overflow: 'hidden',
                flexShrink: 0, border: '1px solid #eee', backgroundColor: '#fafafa',
              }}>
                <img
                  src={item.url}
                  alt={item.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <p style={{
                    margin: 0, fontSize: '14px', fontWeight: 600, color: '#222',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.name}
                  </p>
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#888' }}>
                    Storlek: {item.size}
                  </p>
                </div>

                {/* Quantity + price row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                  {/* Quantity stepper */}
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden',
                  }}>
                    <button
                      onClick={() => handleQuantityChange(item.id, item.size, -1)}
                      style={{
                        width: 32, height: 32, border: 'none', backgroundColor: 'transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <RemoveIcon style={{ fontSize: 16, color: '#555' }} />
                    </button>
                    <span style={{
                      width: 28, textAlign: 'center', fontSize: '13px', fontWeight: 600,
                      color: '#222', userSelect: 'none',
                    }}>
                      {qty}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(item.id, item.size, 1)}
                      style={{
                        width: 32, height: 32, border: 'none', backgroundColor: 'transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <AddIcon style={{ fontSize: 16, color: '#555' }} />
                    </button>
                  </div>

                  {/* Price */}
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#161825' }}>
                    {item.price * qty} kr
                  </span>
                </div>
              </div>

              {/* Remove button */}
              <button
                onClick={() => handleRemoveItem(item.id, item.size)}
                style={{
                  width: 28, height: 28, borderRadius: '8px', border: 'none',
                  backgroundColor: 'transparent', cursor: 'pointer', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background-color 0.15s', alignSelf: 'flex-start',
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <CloseIcon style={{ fontSize: 16, color: '#999' }} />
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Footer / Checkout ── */}
      <div style={{
        flexShrink: 0, borderTop: '1px solid #eee',
        padding: '20px 24px 24px', backgroundColor: '#fafafa',
      }}>
        {/* Subtotal */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 6,
        }}>
          <span style={{ fontSize: '13px', color: '#888' }}>Delsumma</span>
          <span style={{ fontSize: '13px', color: '#888' }}>{totalPrice} kr</span>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 6,
        }}>
          <span style={{ fontSize: '13px', color: '#888' }}>Frakt</span>
          <span style={{ fontSize: '13px', color: freeShipping ? '#16a34a' : '#888', fontWeight: freeShipping ? 600 : 400 }}>
            {freeShipping ? 'Gratis' : '49 kr'}
          </span>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 0', borderTop: '1px solid #e5e5e5', marginTop: 8,
        }}>
          <span style={{ fontSize: '16px', fontWeight: 700, color: '#161825' }}>Totalt</span>
          <span style={{ fontSize: '16px', fontWeight: 700, color: '#161825' }}>
            {totalPrice + (freeShipping ? 0 : 49)} kr
          </span>
        </div>

        {/* Checkout button */}
        <button
          onClick={() => {
            props.setOpenBasketDrawer(false);
            if (props.setOpenOverlayLoad) props.setOpenOverlayLoad(true);
            handleCheckoutClick();
            if (window.fbq) {
              window.fbq('track', 'InitiateCheckout', {
                content_name: "Basket Checkout",
                content_type: 'product',
                value: totalPrice,
                currency: 'SEK',
              });
            }
            if (window.gtag) {
              window.gtag('event', 'initiate_checkout', {
                items: formattedItems,
                value: totalPrice,
                currency: 'SEK',
              });
            }
          }}
          style={{
            width: '100%', padding: '14px 0', borderRadius: '12px',
            border: 'none', backgroundColor: '#161825', color: '#fff',
            fontSize: '15px', fontWeight: 700, cursor: 'pointer',
            transition: 'background-color 0.2s, transform 0.1s',
            letterSpacing: '0.03em',
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2a2d3a'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#161825'}
        >
          Till kassan
        </button>

        {/* Trust signals */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '16px', marginTop: 14,
        }}>
          <span style={{ fontSize: '11px', color: '#aaa' }}>🔒 Säker betalning</span>
          <span style={{ fontSize: '11px', color: '#aaa' }}>📦 Snabb leverans</span>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
